import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  getTemplate,
  createTemplate,
  updateTemplate,
  deleteTemplate,
} from "../../api/templates";
import DayGridEditor from "../DayGridEditor";

const blank = () => ({
  name: "",
  description: "",
  defaultLocationId: "",
  defaultSeasonId: "",
  defaultNights: 7,
  defaultStudentPax: 25,
  defaultVisitingAdultPax: 2,
  isActive: true,
  daySlots: [],
});

export default function TemplateDetail({ templateId, locations, seasons, onClose }) {
  const [form, setForm] = useState(blank());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!templateId) return;
    try {
      setLoading(true);
      const t = await getTemplate(templateId);
      setForm({
        name: t.name || "",
        description: t.description || "",
        defaultLocationId: t.defaultLocationId ?? "",
        defaultSeasonId: t.defaultSeasonId ?? "",
        defaultNights: t.defaultNights ?? 0,
        defaultStudentPax: t.defaultStudentPax ?? 0,
        defaultVisitingAdultPax: t.defaultVisitingAdultPax ?? 0,
        isActive: t.isActive !== false,
        daySlots: Array.isArray(t.daySlots) ? t.daySlots : [],
      });
    } catch (e) {
      setError(e.message || "Failed to load template");
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      defaultLocationId: form.defaultLocationId === "" ? null : Number(form.defaultLocationId),
      defaultSeasonId: form.defaultSeasonId === "" ? null : Number(form.defaultSeasonId),
      defaultNights: Number(form.defaultNights) || 0,
      defaultStudentPax: Number(form.defaultStudentPax) || 0,
      defaultVisitingAdultPax: Number(form.defaultVisitingAdultPax) || 0,
      isActive: !!form.isActive,
      daySlots: (form.daySlots || []).map((s) => ({
        dayIndex: s.dayIndex,
        slot: s.slot,
        productId: s.productId ?? null,
        notes: s.notes ?? null,
      })),
    };
    try {
      setSaving(true);
      setError("");
      if (templateId) await updateTemplate(templateId, body);
      else await createTemplate(body);
      onClose();
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!templateId) return;
    if (!window.confirm(`Soft-delete template "${form.name}"?`)) return;
    try {
      await deleteTemplate(templateId);
      onClose();
    } catch (e) {
      setError(e.message || "Delete failed");
    }
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onClose}>Back</Button>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {templateId ? `Edit Template: ${form.name || ""}` : "New Template"}
        </Typography>
        {templateId && (
          <Button color="error" onClick={remove}>Delete</Button>
        )}
        <Button variant="contained" onClick={save} disabled={saving || loading}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </Stack>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Template defaults</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Name" required fullWidth
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                  label="Description" fullWidth multiline minRows={2}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Default Location</InputLabel>
                  <Select
                    label="Default Location"
                    value={form.defaultLocationId}
                    onChange={(e) => setForm({ ...form, defaultLocationId: e.target.value })}
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {locations.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Default Season</InputLabel>
                  <Select
                    label="Default Season"
                    value={form.defaultSeasonId}
                    onChange={(e) => setForm({ ...form, defaultSeasonId: e.target.value })}
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {seasons.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  label="Default Nights" type="number"
                  inputProps={{ min: 0 }}
                  value={form.defaultNights}
                  onChange={(e) => setForm({ ...form, defaultNights: e.target.value })}
                />
                <TextField
                  label="Default Student Pax" type="number"
                  inputProps={{ min: 0 }}
                  value={form.defaultStudentPax}
                  onChange={(e) => setForm({ ...form, defaultStudentPax: e.target.value })}
                />
                <TextField
                  label="Default Visiting Adult Pax" type="number"
                  inputProps={{ min: 0 }}
                  value={form.defaultVisitingAdultPax}
                  onChange={(e) => setForm({ ...form, defaultVisitingAdultPax: e.target.value })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!form.isActive}
                      onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Day grid</Typography>
              <DayGridEditor
                daySlots={form.daySlots}
                nights={Number(form.defaultNights) || 0}
                onChange={(next) => setForm({ ...form, daySlots: next })}
              />
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
